import {
    BarcodeOutlined,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    FlagFilled,
    FlagOutlined,
    LockTwoTone,
    MoonOutlined,
    PlusOutlined,
    PushpinFilled,
    PushpinOutlined,
    ShareAltOutlined,
    SunOutlined,
    TagOutlined
} from "@ant-design/icons";
import {
    Button,
    Card,
    Divider,
    Flex,
    Image,
    Input,
    Popconfirm,
    Popover,
    Rate,
    Space,
    Spin,
    Tag,
    Tooltip,
    Typography
} from "antd";
import Paragraph from "antd/es/typography/Paragraph";
import { useAtom } from "jotai";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import TextTruncate from "react-text-truncate";
import GooglePlayBooksIcon from "../assets/sprites/GooglePlayBooks.png";
import { IconFeather, IconHeart, IconPepper, IconStar, IconTear } from "../assets/sprites/CustomIcons";
import { GetData, MutateData } from "../handlers/axios";
import { BookId, UserId } from "../handlers/globals";
import { Defaults, QueryCase, QueryId, ToastId } from "../handlers/enums";
import { notifyError, notifySuccess } from "../handlers/extensions";
import ComponentBookIcon from "./components/bookicon";
import ComponentDateTimePicker from "./components/datetimepicker";
import ComponentState from "./components/states";

export function SectionBook() {
    const [bookId] = useAtom(BookId);
    const [searchParams] = useSearchParams();

    const resolvedBookId = !bookId || bookId === Defaults.GuidEmpty ? searchParams.get("id") : bookId;

    if (!resolvedBookId) {
        return <ComponentState error />;
    }

    return <BookLayer bookId={resolvedBookId} />;
}

function BookLayer(props) {
    const [userId] = useAtom(UserId);
    const [, setBookId] = useAtom(BookId);

    useEffect(() => {
        setBookId(props.bookId);
    }, [props.bookId, setBookId]);

    const response = GetData(QueryId.SelectBook, { UserId: userId, BookId: props.bookId });

    if (response.isLoading) {
        return <Spin size="large" className="mx-auto mt-8 block" />;
    }

    if (response.isFalse) {
        return <ComponentState error />;
    }

    if (response.isEmpty) {
        return <ComponentState empty />;
    }

    return <Book book={response.data} refetch={response.refetch} />;
}

function Book(props) {
    const { t } = useTranslation();

    const [userId] = useAtom(UserId);
    const [freezeActions, setFreezeActions] = useState(false);
    const [isCompleted, setIsCompleted] = useState(Boolean(props.book.completedOn));
    const [fullDescription, setFullDescription] = useState(false);
    const [favoChars, setFavoChars] = useState(props.book.favoriteCharacters ?? Defaults.StringEmpty);
    const [notes, setNotes] = useState(props.book.notes ?? Defaults.StringEmpty);

    useEffect(() => {
        setIsCompleted(Boolean(props.book.completedOn));
        setFavoChars(props.book.favoriteCharacters ?? Defaults.StringEmpty);
        setNotes(props.book.notes ?? Defaults.StringEmpty);
    }, [props.book.completedOn, props.book.favoriteCharacters, props.book.notes]);

    const onActionEnd = () => setFreezeActions(false);

    const actionBookmark = MutateData(QueryId.ToggleUserBook, onActionEnd, [
        { case: QueryCase.Success, onCase: () => props.refetch() }
    ]);

    const actionDelete = MutateData(QueryId.ToggleUserBook, onActionEnd, [
        { case: QueryCase.Success, onCase: () => props.refetch() }
    ]);

    const actionToggleComplete = MutateData(QueryId.ToggleUserBookComplete, onActionEnd, [
        { case: QueryCase.Success, onCase: () => props.refetch() },
        {
            case: QueryCase.Error,
            onCase: () => {
                notifyError(ToastId.ActionError);
                setIsCompleted(Boolean(props.book.completedOn));
            }
        }
    ]);

    const actionRate = MutateData(QueryId.UpdateUserBookRating, onActionEnd, [
        { case: QueryCase.Success, onCase: () => { } },
        { case: QueryCase.Error, onCase: () => notifyError(ToastId.ActionError) }
    ]);

    const actionFavoChars = MutateData(QueryId.UpdateUserBookCharacters, onActionEnd, [
        { case: QueryCase.Success, onCase: () => { } },
        { case: QueryCase.Error, onCase: () => notifyError(ToastId.ActionError) }
    ]);

    const actionNotes = MutateData(QueryId.UpdateUserBookNotes, onActionEnd, [
        { case: QueryCase.Success, onCase: () => { } },
        { case: QueryCase.Error, onCase: () => notifyError(ToastId.ActionError) }
    ]);

    const mutateBookAction = (action) => {
        setFreezeActions(true);
        action.mutate({
            UserId: userId,
            BookId: props.book.bookId
        });
    };

    const onRate = (ratingType, rating) => {
        setFreezeActions(true);
        actionRate.mutate({
            UserId: userId,
            BookId: props.book.bookId,
            RatingType: ratingType,
            Rating: rating
        });
    };

    const onSaveFavoChars = () => {
        setFreezeActions(true);
        actionFavoChars.mutate({
            UserId: userId,
            BookId: props.book.bookId,
            Text: favoChars
        });
    };

    const onSaveNotes = () => {
        setFreezeActions(true);
        actionNotes.mutate({
            UserId: userId,
            BookId: props.book.bookId,
            Text: notes
        });
    };

    const { TextArea } = Input;

    return (
        <Space direction="vertical" size={14} className="w-full">
            <Card className="border-white/10 bg-custom-grey/65 shadow-2xl">
                <div className="pb-3">
                    <ComponentBookIcon book={props.book} />
                </div>

                <div className="mt-2 text-center text-custom-white">
                    {props.book.completedOn ? (
                        <Tag color="#87d068" className="mb-4">{t("completed_on")} {dayjs(props.book.completedOn).format("DD-MM-YYYY")}</Tag>
                    ) : null}

                    <Flex justify="center" align="center" gap={8}>
                        <Typography.Title level={3} className="!m-0 !text-custom-white">{props.book.title}</Typography.Title>
                        <a href={props.book.linkPreview} rel="noreferrer noopener" target="_blank">
                            <Image width={32} height={32} className="min-w-[32px] rounded-lg hover:hue-rotate-60" src={GooglePlayBooksIcon} preview={false} />
                        </a>
                    </Flex>

                    <Typography.Text className="!text-lg !text-custom-white/95">{props.book.subtitle}</Typography.Text>
                    <Typography.Paragraph className="!mb-1 !text-sm !text-custom-white/85">{t("by_author")} {String(props.book.authors ?? Defaults.StringEmpty).replace(/,\s*/g, ", ")}</Typography.Paragraph>
                    <Typography.Text className="!text-xs !text-custom-white/75">{dayjs(props.book.publishedOn).format("YYYY")} - {props.book.publisher}</Typography.Text>
                    <Typography.Paragraph className="!my-1 !text-xs !text-custom-white/75">{props.book.categories}</Typography.Paragraph>

                    <Flex justify="center" align="center" gap={8}>
                        <Typography.Text className="!text-xs !text-custom-white/80">
                            {t("page_amount")} {props.book.pageCount === 0 ? t("unknown") : props.book.pageCount}
                        </Typography.Text>
                        <Popover
                            content={
                                <Paragraph copyable={{ tooltips: [t("copy"), t("copied")], text: props.book.isbn }}>
                                    <span className="font-semibold">{t("isbn")}:</span> {props.book.isbn}
                                </Paragraph>
                            }
                        >
                            <BarcodeOutlined className="text-lg text-custom-yellow" />
                        </Popover>
                    </Flex>
                    <Typography.Text className="!mt-1 !block !text-xs !text-custom-white/75">
                        {t("isbn")}: {props.book.isbn || t("unknown")}
                    </Typography.Text>

                    {props.book.description ? (
                        <>
                            <TextTruncate
                                containerClassName="book-description pt-2 text-sm"
                                line={fullDescription ? 9999 : 3}
                                truncateText="…"
                                text={props.book.description}
                                textTruncateChild={
                                    <Button className="!bg-transparent p-0 text-sm !text-custom-antdesign_blue" onClick={() => setFullDescription(true)} size="small" type="link">
                                        {t("read_more")}
                                    </Button>
                                }
                            />
                            {fullDescription ? (
                                <Button
                                    className="!bg-transparent p-0 text-sm !text-custom-antdesign_blue"
                                    onClick={() => {
                                        window.scrollTo(0, 0);
                                        setFullDescription(false);
                                    }}
                                    size="small"
                                    type="link"
                                >
                                    {t("read_less")}
                                </Button>
                            ) : null}
                        </>
                    ) : null}
                </div>

                <Divider className="!my-4 !border-custom-darkgrey" />

                <Space direction="vertical" size={10} className="w-full">
                    <Flex gap={10} justify="center" wrap>
                        <Button onClick={() => { notifySuccess("toast_copied"); navigator.clipboard.writeText(window.location.href); }} disabled={freezeActions} className="bg-[#3b82f6aa] disabled:bg-[#3b82f6aa]" icon={<ShareAltOutlined />} size="large">
                            {t("share")}
                        </Button>

                        {props.book.retailPrice && props.book.linkPurchase ? (
                            <a href={props.book.linkPurchase} rel="noreferrer noopener" target="_blank">
                                <Button disabled={freezeActions} className="bg-[#3b82f6aa] disabled:bg-[#3b82f6aa]" icon={<TagOutlined />} size="large">
                                    {t("buy")} {props.book.retailPrice}&#8364;
                                </Button>
                            </a>
                        ) : null}
                    </Flex>

                    {userId ? (
                        !props.book.userBookId ? (
                            <Flex justify="center">
                                <Button disabled={freezeActions} className="bg-[#3b82f6aa] disabled:bg-[#3b82f6aa]" onClick={() => mutateBookAction(actionBookmark)} icon={<PushpinOutlined />} size="large">
                                    {t("add_tbr")}
                                </Button>
                            </Flex>
                        ) : (
                            <Flex justify="center">
                                <Popconfirm
                                    title={t("confirm_delete_book_title")}
                                    description={t("confirm_delete_book_description")}
                                    onConfirm={() => mutateBookAction(actionDelete)}
                                    okText={t("yes")}
                                    cancelText={t("no")}
                                    okButtonProps={{ className: "!border-none hover:!bg-custom-orange" }}
                                    cancelButtonProps={{ className: "hover:!text-custom-white" }}
                                >
                                    <Button disabled={freezeActions} className="border-none !bg-custom-red disabled:!bg-custom-red active:!bg-custom-red" icon={<PushpinFilled />} size="large">
                                        {t("delete_tbr")}
                                    </Button>
                                </Popconfirm>
                            </Flex>
                        )
                    ) : null}

                    {userId ? (
                        !isCompleted ? (
                            <Flex justify="center">
                                <Popconfirm
                                    title={t("confirm_complete_book_title")}
                                    description={t("confirm_complete_book_description")}
                                    onConfirm={() => {
                                        setIsCompleted(true);
                                        mutateBookAction(actionToggleComplete);
                                    }}
                                    okText={t("yes")}
                                    cancelText={t("no")}
                                    okButtonProps={{ className: "!border-none hover:!bg-custom-orange" }}
                                    cancelButtonProps={{ className: "hover:!text-custom-white" }}
                                >
                                    <Button disabled={freezeActions} className="bg-[#3b82f6aa] disabled:bg-[#3b82f6aa]" icon={<FlagOutlined />} size="large">
                                        {t("complete")}
                                    </Button>
                                </Popconfirm>
                            </Flex>
                        ) : (
                            <Flex justify="center">
                                <Popconfirm
                                    title={t("confirm_uncomplete_book_title")}
                                    description={t("confirm_uncomplete_book_description")}
                                    onConfirm={() => {
                                        setIsCompleted(false);
                                        mutateBookAction(actionToggleComplete);
                                    }}
                                    okText={t("yes")}
                                    cancelText={t("no")}
                                    okButtonProps={{ className: "!border-none hover:!bg-custom-orange" }}
                                    cancelButtonProps={{ className: "hover:!text-custom-white" }}
                                >
                                    <Button disabled={freezeActions} className="border-none !bg-custom-red disabled:!bg-custom-red active:!bg-custom-red" icon={<FlagFilled />} size="large">
                                        {t("uncomplete")}
                                    </Button>
                                </Popconfirm>
                            </Flex>
                        )
                    ) : null}
                </Space>
            </Card>

            {userId ? (
                <Card className="border-white/10 bg-custom-grey/65 shadow-2xl">
                    <Sessions book={props.book} isCompleted={isCompleted} />

                    <Divider className="!my-4 !border-custom-darkgrey" />

                    <div className="text-custom-white">
                        <Typography.Title level={4} className="!mb-4 !text-custom-white">
                            {t("rate")}
                            {isCompleted ? null : (
                                <Tooltip title={t("tooltip_ratinglock")}>
                                    <LockTwoTone className="ml-2 text-xl" twoToneColor="#ff0000" />
                                </Tooltip>
                            )}
                        </Typography.Title>

                        <div className="flex flex-col gap-3">
                            <RatingRow label={t("suspense")} value={props.book.ratingSuspense} disabled={!isCompleted} className="rate-bg-checked-red" icon={IconHeart} onChange={(value) => onRate("Suspense", value)} />
                            <RatingRow label={t("tear")} value={props.book.ratingTears} disabled={!isCompleted} className="rate-bg-checked-blue" icon={IconTear} onChange={(value) => onRate("Tears", value)} />
                            <RatingRow label={t("spicy")} value={props.book.ratingSpiciness} disabled={!isCompleted} className="rate-bg-checked-green" icon={IconPepper} onChange={(value) => onRate("Spiciness", value)} />
                            <RatingRow label={t("story")} value={props.book.ratingStory} disabled={!isCompleted} className="rate-bg-checked-white" icon={IconFeather} onChange={(value) => onRate("Story", value)} />
                            <RatingRow label={t("overall")} value={props.book.ratingOverall} disabled={!isCompleted} className="rate-bg-checked-yellow" icon={IconStar} onChange={(value) => onRate("Overall", value)} />
                        </div>

                        <Flex className="mt-4" gap={10} align="start">
                            <Typography.Text className="max-w-[28%] whitespace-nowrap !font-semibold !text-custom-white">{t("favo_chars")}</Typography.Text>
                            <TextArea
                                value={favoChars}
                                onBlur={onSaveFavoChars}
                                onChange={(event) => setFavoChars(event.target.value)}
                                variant={isCompleted ? "outlined" : "borderless"}
                                readOnly={!isCompleted}
                                placeholder={!isCompleted ? null : t("placeholder_favo_chars")}
                                className="custom-color-placeholder mb-3"
                                showCount={isCompleted}
                                maxLength={1000}
                                autoSize={{ minRows: 2, maxRows: 10 }}
                            />
                        </Flex>

                        <Flex className="mt-2" gap={10} align="start">
                            <Typography.Text className="max-w-[28%] whitespace-nowrap !font-semibold !text-custom-white">{t("notes")}</Typography.Text>
                            <TextArea
                                value={notes}
                                onBlur={onSaveNotes}
                                onChange={(event) => setNotes(event.target.value)}
                                variant={isCompleted ? "outlined" : "borderless"}
                                readOnly={!isCompleted}
                                placeholder={!isCompleted ? null : t("placeholder_notes")}
                                className="custom-color-placeholder mb-3"
                                showCount={isCompleted}
                                maxLength={1000}
                                autoSize={{ minRows: 2, maxRows: 10 }}
                            />
                        </Flex>

                        <Typography.Text className="text-2xs !text-custom-white/70">{t("quotes_coming_soon")}</Typography.Text>
                    </div>
                </Card>
            ) : null}
        </Space>
    );
}

function RatingRow({ label, value, disabled, icon: IconComponent, className, onChange }) {
    return (
        <div className="flex flex-nowrap items-center justify-between gap-3">
            <Typography.Text className="!whitespace-nowrap !font-semibold !text-custom-white !shrink-0">{label}</Typography.Text>
            <Rate
                defaultValue={value}
                disabled={disabled}
                onChange={onChange}
                className={`rate-bg-32 rate-bg-light ${className}`}
                character={<span className="inline-flex"><IconComponent /></span>}
            />
        </div>
    );
}

function Sessions(props) {
    const { t } = useTranslation();

    const [userId] = useAtom(UserId);
    const [freezeActions, setFreezeActions] = useState(false);

    const response = GetData(QueryId.SelectUserBookSessions, { UserId: userId, BookId: props.book.bookId });

    const actionAddSession = MutateData(QueryId.InsertUserBookSession, () => {
        setFreezeActions(false);
    }, [
        { case: QueryCase.Success, onCase: () => response.refetch() },
        { case: QueryCase.Error, onCase: () => notifyError(ToastId.ActionError) }
    ]);

    const onAdd = () => {
        setFreezeActions(true);
        actionAddSession.mutate({
            BookId: props.book.bookId,
            UserId: userId
        });
    };

    return (
        <Card
            className="card-sessions border-none bg-none"
            title={t("reading_sessions")}
            extra={props.isCompleted || response.isLoading || response.isFalse || response.data?.some((session) => !session.endedOn) ? null : <Button disabled={freezeActions} type="primary" shape="circle" className="bg-custom-orange disabled:bg-custom-orange" icon={<PlusOutlined />} onClick={onAdd} />}
        >
            {props.isCompleted ? (
                <Typography.Text>{t("sessions_empty")}</Typography.Text>
            ) : (
                <div className="flex flex-col gap-2">
                    {response.isLoading ? <Spin size="large" /> : response.isFalse ? <ComponentState error /> : response.isEmpty ? <ComponentState empty description={t("empty_sessions")} /> : response.data?.map((session) => <Session key={session.id} session={session} onChanged={response.refetch} />)}
                </div>
            )}
        </Card>
    );
}

function Session(props) {
    const { t } = useTranslation();
    const [userId] = useAtom(UserId);
    const [bookId] = useAtom(BookId);

    const [freezeActions, setFreezeActions] = useState(false);

    const [hidden, setHidden] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [startedOn, setStartedOn] = useState(props.session?.startedOn);
    const [endedOn, setEndedOn] = useState(props.session?.endedOn);

    const [startOpen, setStartOpen] = useState(false);
    const [endOpen, setEndOpen] = useState(false);

    const actionAdjustSession = MutateData(QueryId.UpdateUserBookSession, () => {
        setFreezeActions(false);
    }, [
        { case: QueryCase.Success, onCase: () => props.onChanged?.() },
        { case: QueryCase.Error, onCase: () => notifyError(ToastId.ActionError) }
    ]);

    const actionDeleteSession = MutateData(QueryId.DeleteUserBookSession, () => {
        setFreezeActions(false);
    }, [
        {
            case: QueryCase.Success,
            onCase: () => {
                setHidden(true);
                props.onChanged?.();
            }
        },
        {
            case: QueryCase.Error,
            onCase: () => {
                notifyError(ToastId.ActionError);
                setHidden(false);
            }
        }
    ]);

    const onFinish = () => {
        setFreezeActions(true);
        actionAdjustSession.mutate({
            StartedOn: startedOn,
            EndedOn: endedOn,
            SessionId: props.session.id,
            BookId: bookId,
            UserId: userId
        });
    };

    const onDelete = () => {
        setFreezeActions(true);
        actionDeleteSession.mutate({
            SessionId: props.session.id,
            BookId: bookId,
            UserId: userId
        });
    };

    if (hidden) {
        return null;
    }

    if (isEditing) {
        return (
            <div className="flex justify-between gap-2">
                <div className="w-full">
                    <ComponentDateTimePicker open={startOpen} value={startedOn} cancel={() => setStartOpen(false)} confirm={(event) => { setStartOpen(false); setStartedOn(event); }} />
                    <Input suffix={<Button disabled={freezeActions} type="primary" shape="circle" className="bg-custom-orange disabled:bg-custom-orange" icon={<EditOutlined />} onClick={() => setStartOpen(true)} />} readOnly className="session-input mb-2" prefix={<SunOutlined className="mr-1" />} placeholder={Defaults.DateTimeFormatEurope} value={startedOn ? dayjs(startedOn).format(Defaults.DateTimeFormatEurope) : Defaults.StringEmpty} allowClear />

                    <ComponentDateTimePicker open={endOpen} value={endedOn} cancel={() => setEndOpen(false)} confirm={(event) => { setEndOpen(false); setEndedOn(event); }} />
                    <Input suffix={<Button disabled={freezeActions} type="primary" shape="circle" className="bg-custom-orange disabled:bg-custom-orange" icon={<EditOutlined />} onClick={() => setEndOpen(true)} />} readOnly className="session-input" prefix={<MoonOutlined className="mr-1" />} placeholder={Defaults.DateTimeFormatEurope} value={endedOn ? dayjs(endedOn).format(Defaults.DateTimeFormatEurope) : Defaults.StringEmpty} allowClear />
                </div>

                <div className="flex flex-col gap-2">
                    <Button
                        disabled={freezeActions}
                        type="primary"
                        shape="circle"
                        className="bg-custom-orange disabled:bg-custom-orange"
                        icon={<CloseOutlined />}
                        onClick={() => {
                            onFinish();
                            setIsEditing(false);
                        }}
                    />
                    <Popconfirm
                        title={t("confirm_delete_book_session")}
                        description={t("confirm_delete_book_session_description")}
                        onConfirm={() => {
                            onDelete();
                            setIsEditing(false);
                        }}
                        okText={t("yes")}
                        cancelText={t("no")}
                        okButtonProps={{ className: "!border-none hover:!bg-custom-orange" }}
                        cancelButtonProps={{ className: "hover:!text-custom-white" }}
                    >
                        <Button disabled={freezeActions} type="primary" shape="circle" className="border-none !bg-custom-red disabled:!bg-custom-red" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-custom-grey/50 px-2 py-1">
            <Typography.Text className="!text-custom-white"><SunOutlined className="mr-1" />{startedOn ? dayjs(startedOn).format(Defaults.DateTimeFormatEurope) : t("unknown_datetime")}</Typography.Text>
            <Typography.Text className="!text-custom-white"><MoonOutlined className="mr-1" />{endedOn ? dayjs(endedOn).format(Defaults.DateTimeFormatEurope) : t("unknown_datetime")}</Typography.Text>
            <Button disabled={freezeActions} type="primary" shape="circle" className="bg-custom-orange disabled:bg-custom-orange" icon={<EditOutlined />} onClick={() => setIsEditing(true)} />
        </div>
    );
}
